import { Tab, Tabs, withStyles, styled } from "@material-ui/core";
import { useCollection, useIO, useLinked } from "@type-r/react";
import { Button, Column, Columns, div, Loading } from "lqx-react";
import React, { useEffect } from "react";
import { Policy } from "../../api/policy";
import { PolicyChange } from "../../api/policyChange";
import { BackButton, CancelButton, ConfirmationButton, RightAlignedButtons, WorkflowBody, WorkflowFooter, WorkflowTitle } from "../../shared";
import { PolicyDetailsForm } from "./policy-details-form";
import { useModel } from '@type-r/react';
import { PolicyParametersForm } from "./policy-parameters-form";
import { PolicyPremiumAndReportingForm } from "./policy-premium-and-reporting-form";
import { PolicyBuyersWorkflow } from "./buyers/policy-buyers-workflow";
import { Events } from '@type-r/models';

export const ConfirmPolicyDataStep = ({ jobId, store, cancel, back }: { jobId, store?, cancel?, back?}) => {
    const policyChanges = useCollection.of(PolicyChange);
    const policy = useModel(Policy);
    const $selectedTab = useLinked("policyDetails");
    const $validationRequests = useLinked(0);

    //jobId = "2cd0aff1-c130-4313-b11f-b6861bc449e0";

    useEffect(() => {
        if (policyChanges.models.length <= 0) return;

        policy.transaction(() => {
            policyChanges.models[ 0 ].syncTo(policy);
        });
        if (!policy.id) policy.id = policyChanges.models[ 0 ].id;
    }, [ policyChanges[ '_changeToken' ] ]);

    useIO(async () => {
        await policyChanges.models[ 0 ].validateAs(policy)
    }, [ $validationRequests.value ]);

    const isReady = useIO(async () => {
        await policyChanges.fetch({ params: { jobId } }).catch(e => console.error(e));
    });

    const $doUpdatePolicyIO = useLinked(false);
    const isUpdatePolicyReady = useIO(
        async () => $doUpdatePolicyIO.isTruthy && await updatePolicy(),
        [ $doUpdatePolicyIO.value ]
    );

    const updatePolicy = async () => {
        try {
            policyChanges.models[ 0 ].syncFrom(policy);
            return await policyChanges.models[ 0 ].save();
        } finally {
            $doUpdatePolicyIO.set(false);
        }
    }
    const $doUSaveDraftIO = useLinked(false);
    const isSaveDraftReady = useIO(
        async () => $doUSaveDraftIO.isTruthy && await saveDraft(),
        [ $doUSaveDraftIO.value ]
    );

    const saveDraft = async () => {
        try {
            policyChanges.models[ 0 ].syncFrom(policy);
            await policyChanges.models[ 0 ].save();
            Events.trigger('navigate', '/digitization/insurancepolicies');
        } finally {
            $doUSaveDraftIO.set(false);
        }
    }

    const validatePolicy = () => $validationRequests.set($validationRequests.value + 1);

    return policy.isLoaded && isReady && isUpdatePolicyReady && isSaveDraftReady ? <>
        <WorkflowTitle>
            Step 3: Let's confirm your policy data
        </WorkflowTitle>
        <WorkflowBody>
            <EditPolicyFormContainer>
                <PolicyTabs value={ $selectedTab.value }
                    variant="scrollable"
                    scrollButtons="on"
                    onChange={ (evt, newValue) => $selectedTab.set(newValue) } >
                    <PolicyTab value="policyDetails" label="Policy Details" />
                    <PolicyTab value="policyParameters" label="Policy Parameters" />
                    <PolicyTab value="premiumAndReporting" label="Premium &amp; Reporting" />
                    <PolicyTab value="buyers" label="Buyers" />
                    <PolicyTab value="countrySchedule" label="Country Schedule" />
                </PolicyTabs>
                <PolicyTabContent>
                    { $selectedTab.value === 'policyDetails' && <PolicyDetailsForm policy={ policy } validate={ validatePolicy } /> }
                    { $selectedTab.value === 'policyParameters' && <PolicyParametersForm policy={ policy } validate={ validatePolicy } /> }
                    { $selectedTab.value === 'premiumAndReporting' && <PolicyPremiumAndReportingForm policy={ policy } validate={ validatePolicy } /> }
                    { $selectedTab.value === 'buyers' && <PolicyBuyersWorkflow policy={ policy } /> }
                </PolicyTabContent>
            </EditPolicyFormContainer>
        </WorkflowBody>
        <WorkflowFooter>
            <Columns>
                <Column>
                    <CancelButton color="primary" onClick={ cancel }>Cancel</CancelButton>
                </Column>
                <Column>
                    <RightAlignedButtons>
                        <SaveAsDraftButton color="primary" onClick={ $doUSaveDraftIO.true } >Save as draft</SaveAsDraftButton>
                        <BackButton color="primary" onClick={ back }>Back: Add Policy</BackButton>
                        <ConfirmationButton onClick={ store } >Confirm and add policy</ConfirmationButton>
                    </RightAlignedButtons>
                </Column>
            </Columns>
        </WorkflowFooter>
    </> : <Loading />
};

const PolicyTabs = withStyles({
    indicator: {
        display: 'flex',
        marginLeft: '0px',
        paddingLeft: '0px',
        backgroundColor: '1890ff',
        '& > span': {
            width: '100%',
            backgroundColor: '#1890ff',
        },
    },
})((props: any) => <Tabs { ...props } TabIndicatorProps={ { children: <span /> } } />);

const PolicyTab = withStyles({
    root: {
        textTransform: 'none',
        color: '#4197d6',
        fontFamily: 'Montserrat',
        fontWeight: 600,
        fontSize: 13,
        marginRight: 10,
        opacity: 1,
        marginLeft: '0px',
        paddingLeft: '0px',
    },
})((props: any) => <Tab disableRipple { ...props } />);

const PolicyTabContent = div({
    marginTop: '10px'
});

const EditPolicyFormContainer = div({
    background: '#FFFFFF',
    borderRadius: '4px',
    padding: '30px',
    paddingBottom: '250px'
});

const SaveAsDraftButton = styled(Button)({
    minWidth: '200px',
    minHeight: '25px',
    marginBottom: '10px',
});
